import React from 'react';
import { useAuth } from '../AuthContext';
import { APP_VERSION, LAST_ACKNOWLEDGED_VERSION_KEY, compareVersions, isNewer, parseVersion } from '../appVersion';
import useReleaseNotes from '../Hooks/useReleaseNotes';
import ModalShell from './Common/ModalShell';
import ReleaseNotesList from './ReleaseNotesList';

function readAcknowledged() {
  try {
    return window.localStorage.getItem(LAST_ACKNOWLEDGED_VERSION_KEY);
  } catch {
    return null;
  }
}

function writeAcknowledged(version) {
  try {
    window.localStorage.setItem(LAST_ACKNOWLEDGED_VERSION_KEY, version);
  } catch {
    // Storage blocked (private mode, quota): the modal may show again, which is harmless.
  }
}

function isStaffUser(user) {
  if (!user) return false;
  const isPublic = (user.user_type || 'staff') === 'public';
  return !isPublic && user.role !== 0;
}

/**
 * Staff-only "What's New" dialog shown once after the client version changes.
 * The first visit on a device just records the current version.
 */
export default function WhatsNewModal() {
  const { user } = useAuth();
  const isStaff = isStaffUser(user);
  const hasRealVersion = Boolean(parseVersion(APP_VERSION));

  const [acknowledged, setAcknowledged] = React.useState(() => readAcknowledged());
  const [closed, setClosed] = React.useState(false);

  const shouldCheck =
    isStaff && hasRealVersion && Boolean(parseVersion(acknowledged)) && isNewer(APP_VERSION, acknowledged);

  // First visit on this device (or an unreadable stored value): record silently.
  React.useEffect(() => {
    if (!isStaff || !hasRealVersion) return;
    if (!parseVersion(acknowledged)) {
      writeAcknowledged(APP_VERSION);
      setAcknowledged(APP_VERSION);
    }
  }, [isStaff, hasRealVersion, acknowledged]);

  const { status, releases } = useReleaseNotes({ since: acknowledged, enabled: shouldCheck });

  const relevant = React.useMemo(
    () =>
      releases
        .filter((r) => isNewer(r.version, acknowledged) && compareVersions(r.version, APP_VERSION) <= 0)
        .sort((a, b) => compareVersions(b.version, a.version)),
    [releases, acknowledged]
  );

  const acknowledge = React.useCallback(() => {
    writeAcknowledged(APP_VERSION);
    setClosed(true);
  }, []);

  // Nothing to say (backend unreachable or no notes for this range): acknowledge quietly.
  React.useEffect(() => {
    if (!shouldCheck) return;
    if (status === 'error' || (status === 'ready' && relevant.length === 0)) {
      writeAcknowledged(APP_VERSION);
    }
  }, [shouldCheck, status, relevant.length]);

  if (!shouldCheck || closed || status !== 'ready' || relevant.length === 0) return null;

  return (
    <ModalShell
      onClose={acknowledge}
      title={`What's new in v${APP_VERSION}`}
      description={
        relevant.length > 1 ? `Changes in the ${relevant.length} updates since you last checked.` : undefined
      }
      width="md"
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={acknowledge}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Got it
          </button>
        </div>
      }
    >
      <ReleaseNotesList releases={relevant} />
    </ModalShell>
  );
}
